import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';

import MainContext from 'context/main';
import translations from 'i18n';

import DropdownLink from './dropdown-link';

const hasIsBordered = (array) => array.some((obj) => obj.menuItem.isBordered);

const DropdownMenu = ({ items, ids, activeDropdownLabel }) => {
  const { language } = useContext(MainContext);
  const locale = language?.locale || 'en';

  return (
    <div
      className={clsx(
        'absolute bottom-[22px] z-20 flex translate-y-full overflow-hidden rounded-2xl border border-gray-4 bg-white shadow-icon',
        {
          '-left-[29px] min-w-[596px]':
            activeDropdownLabel === translations[locale].mainMenu.features,
          'left-[119px] min-w-[516px]':
            activeDropdownLabel === translations[locale].mainMenu.solutions,
          'left-[236px] min-w-[516px]':
            activeDropdownLabel === translations[locale].mainMenu.developers,
          'left-[339px] min-w-[556px]':
            activeDropdownLabel === translations[locale].mainMenu.resources,
          'left-[319px] min-w-[516px]':
            activeDropdownLabel === translations[locale].mainMenu.integrations,
        }
      )}
    >
      {hasIsBordered(items) ? (
        <div className="grid grid-cols-[212px_1fr]">
          <div className="border-r border-[#e2e2e7] bg-new-gray-2 px-6 py-[19px]">
            <p className="mb-4 text-[11px] font-medium uppercase leading-none -tracking-tight-2 text-gray-8/80">
              {items[0].label}
            </p>
            <ul className="grid gap-4">
              {items[0].childItems.nodes.map(({ label, path, menuItem }, index) => (
                <li key={label}>
                  <DropdownLink
                    id={ids[0].childItems[index].id}
                    label={label}
                    path={path}
                    iconName={menuItem.iconName}
                    activeDropdownLabel={activeDropdownLabel}
                  />
                </li>
              ))}
            </ul>
          </div>
          <ul className="grid grid-cols-[184px_122px_180px] gap-x-12 gap-y-8 px-6 py-[18px]">
            {items[1].childItems.nodes.map(({ label, childItems }, index) => (
              <li className="" key={label}>
                <p className="mb-4 text-[11px] font-medium uppercase leading-none -tracking-tight-2 text-gray-8/80">
                  {label}
                </p>
                <ul className="grid gap-4">
                  {childItems.nodes.map(({ label, path, menuItem }, linkIndex) => (
                    <li className="" key={label}>
                      <DropdownLink
                        id={ids[1].childItems[index].childItems[linkIndex].id}
                        label={label}
                        path={path}
                        iconName={menuItem.iconName}
                        description={menuItem.description}
                        activeDropdownLabel={activeDropdownLabel}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <ul
          className={clsx('grid w-full', {
            'gap-y-0.5': activeDropdownLabel === translations[locale].mainMenu.features,
          })}
        >
          {items.map(({ label, menuItem, childItems }, index) => (
            <li
              key={label}
              className={clsx('px-6', {
                'pb-6 pt-5': items.length === 1,
                'py-4': items.length > 1,
                '!pt-5': items.length > 1 && index === 0,
                '!pb-5': items.length > 1 && index === items.length - 1,
                'border-b border-dashed border-[#e2e2e7]': index !== items.length - 1,
              })}
            >
              {!menuItem.isRowWrapper && (
                <p className="mb-3.5 text-[11px] font-medium uppercase leading-none tracking-[0.01em] text-gray-8/80">
                  {label}
                </p>
              )}
              <ul
                className={clsx('grid auto-rows-min grid-cols-2 gap-x-12 gap-y-4', {
                  '!gap-x-10 !gap-y-3.5':
                    activeDropdownLabel === translations[locale].mainMenu.features,
                  '!grid-cols-[repeat(2,minmax(0,235px))] !gap-x-10':
                    activeDropdownLabel === translations[locale].mainMenu.resources,
                })}
              >
                {childItems.nodes.map(({ label, path, menuItem }, linkIndex) => (
                  <li key={label}>
                    <DropdownLink
                      id={ids[index].childItems[linkIndex].id}
                      label={label}
                      path={path}
                      iconName={menuItem.iconName}
                      description={menuItem.description}
                      activeDropdownLabel={activeDropdownLabel}
                    />
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

DropdownMenu.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      description: PropTypes.string,
      path: PropTypes.string,
      target: PropTypes.string,
      menuItem: PropTypes.shape({
        iconName: PropTypes.string,
      }),
      childItems: PropTypes.shape({
        nodes: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            description: PropTypes.string,
            path: PropTypes.string,
            target: PropTypes.string,
            menuItem: PropTypes.shape({
              iconName: PropTypes.string,
            }),
          })
        ),
      }),
    })
  ).isRequired,
  ids: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      childItems: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          childItems: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
        })
      ),
    })
  ).isRequired,
  activeDropdownLabel: PropTypes.string.isRequired,
};

export default DropdownMenu;
